<template>
  <div>
    <div class="flex flex-col items-center my-8">
      <div class="mb-4">
        <MoskitoPieScore
          :score="report.lighthouseResult.categories.performance.score * 100"
        />
      </div>
      <div class="text-2xl text-gray-800 mb-4">
        {{ report.lighthouseResult.finalUrl }}
      </div>
      <div>
        <PagespeedColorScoreComponent />
      </div>
    </div>
    <div class="flex flex-wrap flex-row-reverse sm:flex-row">
      <div class="w-full sm:w-2/3">
        <div class="mb-8">
          <h3>
            {{ $t("editPage.header:performance") }}
          </h3>
          <div>
            <div class="flex flex-wrap justify-center mr-2">
              <div
                v-for="audit in promotedAudits"
                :key="audit.id"
                class="w-full sm:w-1/2"
              >
                <div class="audit-item-wrapper">
                  <PagespeedAuditMetricItem
                    :audit="audit"
                    :header="audit.header"
                    :description="audit.description"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h3>
            {{ $t("editPage.header:opportunities") }}
          </h3>
          <div>
            <div v-for="audit in opportunityAudits" :key="audit.id">
              <div class="w-full audit-item-wrapper">
                <PagespeedAuditOpportunityItem :audit="audit" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full sm:w-1/3">
        <div class="screenshot-container">
          <img
            class="block m-auto"
            :src="finalScreenshotSrc"
            :alt="
              `${$t('editPage.screenshot:altText', {
                url: report.lighthouseResult.finalUrl,
              })}`
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import get from "lodash/get";
import sortBy from "lodash/sortBy";

import MoskitoPieScore from "@/wp-pages/edit-page/components/MoskitoPieScore";
import PagespeedAuditMetricItem from "@/wp-pages/edit-page/components/PagespeedAuditMetricItem";
import PagespeedAuditOpportunityItem from "@/wp-pages/edit-page/components/PagespeedAuditOpportunityItem";
import PagespeedColorScoreComponent from "@/wp-pages/edit-page/components/PagespeedColorScoreComponent";

const promotedAuditsData = [
  {
    auditId: "first-meaningful-paint",
    translationHeaderKey: "pagespeed.firstMeaningfulPaint:header",
    translationBodyKey: "pagespeed.firstMeaningfulPaint:body",
  },
  {
    auditId: "first-contentful-paint",
    translationHeaderKey: "pagespeed.firstContentfulPaint:header",
    translationBodyKey: "pagespeed.firstContentfulPaint:body",
  },
  {
    auditId: "server-response-time",
    translationHeaderKey: "pagespeed.serverResponseTime:header",
    translationBodyKey: "pagespeed.serverResponseTime:body",
  },
  {
    auditId: "first-cpu-idle",
    translationHeaderKey: "pagespeed.firstCpuIdle:header",
    translationBodyKey: "pagespeed.firstCpuIdle:body",
  },
  {
    auditId: "interactive",
    translationHeaderKey: "pagespeed.timeToInteractive:header",
    translationBodyKey: "pagespeed.timeToInteractive:body",
  },
  {
    auditId: "speed-index",
    translationHeaderKey: "pagespeed.speedIndex:header",
    translationBodyKey: "pagespeed.speedIndex:body",
  },
];

export default {
  name: "PagespeedReport",
  components: {
    MoskitoPieScore,
    PagespeedAuditMetricItem,
    PagespeedAuditOpportunityItem,
    PagespeedColorScoreComponent,
  },
  data() {
    return { promotedAuditsData };
  },
  computed: {
    promotedAudits() {
      const promotedAuditIds = Object.values(promotedAuditsData).map(
        (x) => x.auditId,
      );
      return Object.entries(this.report.lighthouseResult.audits)
        .map(([, value]) => value)
        .filter((x) => promotedAuditIds.includes(x.id))
        .map((audit) => {
          const auditData = promotedAuditsData.find(
            (x) => x.auditId === audit.id,
          );
          audit.header = this.$t(auditData.translationHeaderKey);
          audit.description = this.$t(auditData.translationBodyKey);
          return audit;
        });
    },
    opportunityAudits() {
      const wordpressTips = get(
        this.report.lighthouseResult.stackPacks.find(
          (x) => x.id === "wordpress",
        ),
        ["descriptions"],
        [],
      );

      const audits = Object.entries(this.report.lighthouseResult.audits)
        .map(([, value]) => value)
        .filter(
          (x) =>
            get(x, ["details", "type"]) === "opportunity" &&
            get(x, ["details", "overallSavingsBytes"], 0) +
              get(x, ["details", "overallSavingsMs"], 0) >
              0,
        )
        .map((x) => {
          const wordpressTip = get(wordpressTips, [x.id], "");
          return {
            ...x,
            wordpressTip,
          };
        });
      return sortBy(
        audits,
        (x) =>
          get(x, ["details", "overallSavingsBytes"], 0) +
          get(x, ["details", "overallSavingsMs"], 0),
      );
    },
    finalScreenshotSrc() {
      return this.report.lighthouseResult.audits["final-screenshot"].details
        .data;
    },
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
  },
};
</script>

<style scoped>
.audit-item-wrapper {
  box-shadow: 0px 2px 11px 1px rgba(0, 0, 0, 0.04) !important;
  padding: 20px 10px 20px 10px !important;
  border-radius: 5px;
  margin-bottom: 10px !important;
  border: 2px solid #fafafa;
}
.screenshot-container {
  margin: 0 !important;
  padding: 10px;
  color: #000;
}
</style>
